import { useEffect, useMemo, useRef, useState, Children, isValidElement, type ReactNode } from "react";
import {
  AppButton,
  FormField,
  StatusChip,
  FormSection,
  toast,
} from "../../../../components/ui";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useFormik } from "formik";
import { matchIsValidTel, MuiTelInput } from "mui-tel-input";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import axios from "axios";
import {
  Category,
  FileType,
  OpenAPI,
  ApplicationRole,
  UserCreateRequest,
  UsersService,
  UserViewModel,
  ZoomService,
  ZoomUserResponse,
} from "../../../../api";
import EducationTable from "../../../../components/basic/EducationTable";
import { HelpersService } from "../../../../services/Helpers";
import {
  startSpinner,
  stopSpinner,
} from "../../../../redux/actions/spinnerActions";
import {
  updateUserData,
  updateUserPhoto,
} from "../../../../redux/actions/userActions";
import { ROLE_LABELS } from "../shared/roleLabels";

const BAR_AND_TRADE_ORGS = [
  "Law organization",
  "South Trade Organization",
];

function formatZoomUserLabel(
  user: Pick<
    UserViewModel,
    "zoomUserName" | "zoomPhoneNumber" | "zoomExtPhoneNumber"
  >
) {
  const name = user.zoomUserName?.trim();
  if (name) return name;
  const phone = user.zoomPhoneNumber?.trim();
  if (phone) return phone;
  return "—";
}

function sameZoomUser(
  a: Pick<ZoomUserResponse, "name" | "phoneNumber" | "extPhoneNumber">,
  b: Pick<ZoomUserResponse, "name" | "phoneNumber" | "extPhoneNumber">
) {
  return (
    (a.phoneNumber ?? "") === (b.phoneNumber ?? "") &&
    (a.extPhoneNumber ?? "") === (b.extPhoneNumber ?? "") &&
    (a.name ?? "").trim() === (b.name ?? "").trim()
  );
}

type ProfileFormValues = UserViewModel & {
  password?: string;
  confirmPassword?: string;
};

type UserProfileFormProps = {
  user: UserViewModel;
  isEdit: boolean;
  isAttorney: boolean;
  /** Create a new attorney account (register). */
  isNew?: boolean;
  /** When true, persist updates into the logged-in Redux user. */
  updateStored?: boolean;
  /** When false, roles are read-only (e.g. own profile). Defaults to true. */
  allowRoleEdit?: boolean;
  /**
   * When true (default), render flat sections for embedding inside a parent card
   * (User Details / Profile settings). When false, use intake-style FormSection cards.
   */
  embedded?: boolean;
  onSave?: () => void;
  onCancelEdit?: () => void;
};

const phoneOptional = yup
  .string()
  .nullable()
  .transform((value) => {
    if (!value?.trim()) return null;
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 1) return null;
    return value;
  })
  .test("is-valid-phone", "Invalid phone", (value) =>
    !value || matchIsValidTel(value)
  );

const staffSchema = yup.object().shape({
  firstName: yup.string().required("Required"),
  lastName: yup.string().required("Required"),
  email: yup.string().email("Invalid email").required("Required"),
  phoneNumber: phoneOptional,
  roles: yup
    .array()
    .of(yup.string())
    .min(1, "Select at least one role")
    .required("Required"),
});

const attorneySchema = staffSchema.shape({
  companyName: yup.string().required("Required"),
  practiceArea: yup.string().required("Required"),
  state: yup.string().required("Required"),
  bio: yup.string().required("Required"),
  barAdmissionYear: yup.number().required("Required"),
  education: yup.array().min(1, "At least one education entry is required"),
});

const attorneyCreateSchema = attorneySchema.shape({
  password: yup
    .string()
    .required("Required")
    .min(6, "At least 6 characters"),
  confirmPassword: yup
    .string()
    .required("Required")
    .oneOf([yup.ref("password")], "Passwords must match"),
});

function ReadOnlyField({ label, value }: { label: string; value?: ReactNode }) {
  const empty = value === undefined || value === null || value === "";
  return (
    <Box sx={{ minWidth: 0, minHeight: 44 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "text.secondary",
          mb: 0.5,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      {empty ? (
        <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
          —
        </Typography>
      ) : typeof value === "string" || typeof value === "number" ? (
        <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
          {value}
        </Typography>
      ) : (
        <Box sx={{ color: "text.primary", typography: "body2", lineHeight: 1.5 }}>
          {value}
        </Box>
      )}
    </Box>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        color: "text.secondary",
        m: 0,
        mb: 2,
        pb: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        lineHeight: 1.4,
        minHeight: 36,
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      {children}
    </Typography>
  );
}

const AVATAR_SIZE = 144;
const COLUMN_GAP = 3;
const FIELD_GAP = 2;

const sectionColumnSx = {
  minWidth: 0,
  width: "100%",
} as const;

function SectionsLayout({
  isAttorney,
  account,
  professional,
}: {
  isAttorney: boolean;
  account: ReactNode;
  professional?: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: isAttorney ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
        },
        gap: COLUMN_GAP,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box sx={sectionColumnSx}>{account}</Box>
      {isAttorney && professional != null && (
        <Box sx={sectionColumnSx}>{professional}</Box>
      )}
    </Box>
  );
}

function AccountSplit({
  avatar,
  fields,
  avatarPt = 0,
}: {
  avatar: ReactNode;
  fields: ReactNode;
  avatarPt?: number | Record<string, number>;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "minmax(0, 1fr) minmax(0, 1fr)",
        },
        gap: COLUMN_GAP,
        width: "100%",
        minWidth: 0,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          pt: avatarPt,
          minWidth: 0,
        }}
      >
        {avatar}
      </Box>
      <Box sx={{ minWidth: 0, width: "100%" }}>{fields}</Box>
    </Box>
  );
}

function FieldsGrid({
  children,
  layout = "grid",
}: {
  children: ReactNode;
  /** Match intake forms: half-width fields on md+ via Bootstrap columns. */
  layout?: "grid" | "intake";
}) {
  if (layout === "intake") {
    return (
      <div className="row g-3">
        {Children.toArray(children).map((child, index) => {
          if (!isValidElement(child)) {
            return child;
          }
          const sx = child.props?.sx as { gridColumn?: string } | undefined;
          const isFullWidth = sx?.gridColumn === "1 / -1";
          return (
            <div
              key={index}
              className={isFullWidth ? "col-12" : "col-12 col-md-6"}
              style={{ minWidth: 0 }}
            >
              {child}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "minmax(0, 1fr) minmax(0, 1fr)",
        },
        gap: FIELD_GAP,
        width: "100%",
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
}

const fullWidthFieldSx = { gridColumn: "1 / -1", minWidth: 0 } as const;

function getInitials(user: UserViewModel) {
  const first = user.firstName?.[0] ?? "";
  const last = user.lastName?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "?";
}

export default function UserProfileForm({
  user,
  isEdit,
  isAttorney,
  isNew = false,
  updateStored = false,
  allowRoleEdit = true,
  embedded = true,
  onSave,
  onCancelEdit,
}: UserProfileFormProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState(user.photo?.url ?? "");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [zoomUsers, setZoomUsers] = useState<ZoomUserResponse[]>([]);

  const lockStaffIdentity =
    !isNew && updateStored && !!user.roles?.includes(ApplicationRole.STAFF);
  const lockEmail = !isNew && updateStored;

  useEffect(() => {
    setSelectedImage(null);
  }, [user.id]);

  useEffect(() => {
    if (!allowRoleEdit || !isEdit || isNew) {
      return;
    }
    ZoomService.getApiZoomUsers()
      .then(setZoomUsers)
      .catch(() => {
        setZoomUsers([]);
        toast.error("Could not load Zoom users.");
      });
  }, [allowRoleEdit, isEdit, isNew]);

  useEffect(() => {
    if (!selectedImage) {
      setPreviewUrl(user.photo?.url ?? "");
      return;
    }
    const objectUrl = URL.createObjectURL(selectedImage);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedImage, user.photo?.url]);

  const onSubmit = async (values: ProfileFormValues) => {
    dispatch(startSpinner());
    try {
      if (isNew || !values.id) {
        const createPayload: UserCreateRequest = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber,
          password: values.password ?? "",
          confirmPassword: values.confirmPassword ?? "",
          roles: [ApplicationRole.ATTORNEY],
          practiceArea: values.practiceArea,
          city: values.city,
          state: values.state,
          address: values.address,
          companyName: values.companyName,
          bio: values.bio,
          education: values.education,
          awardsAchievements: values.awardsAchievements,
          barAdmissionYear: values.barAdmissionYear,
          barAndTradeOrgs: values.barAndTradeOrgs,
          isPending: false,
        };

        const response = await UsersService.postApiUsersRegister(createPayload);
        if (!response.success) {
          toast.error(response.message || "Error creating attorney");
          return;
        }

        if (selectedImage && response.userId) {
          const formData = new FormData();
          formData.append("files", selectedImage);
          const url = `${OpenAPI.BASE}/api/File/${response.userId}/upload`;
          await axios
            .post(url, formData, {
              params: { type: FileType.ACCOUNT_PHOTO },
            })
            .catch(() => {
              toast.error("Error uploading file.");
            });
        }

        toast.success("Attorney created");
        onSave?.();
        navigate("/attorneys");
        return;
      }

      const payload = allowRoleEdit
        ? values
        : { ...values, roles: user.roles ?? values.roles };
      const updatedUser = await UsersService.putApiUsers(payload.id!, payload);
      if (updateStored) {
        dispatch(updateUserData(updatedUser));
      }
      if (selectedImage) {
        const formData = new FormData();
        formData.append("files", selectedImage);
        const url = `${OpenAPI.BASE}/api/File/${payload.id}/upload`;
        const response = await axios.post(url, formData, {
          params: { type: FileType.ACCOUNT_PHOTO },
        });
        if (updateStored) {
          dispatch(updateUserPhoto(response.data));
        }
      }
      toast.success("User data updated");
      onSave?.();
    } catch {
      toast.error(
        isNew
          ? "Error creating attorney. Please make sure that all fields are valid."
          : "Error updating user data. Please make sure that all fields are valid."
      );
    } finally {
      dispatch(stopSpinner());
    }
  };

  const validationSchema = isNew
    ? attorneyCreateSchema
    : isAttorney
      ? attorneySchema
      : staffSchema;

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik<ProfileFormValues>({
    enableReinitialize: true,
    initialValues: {
      ...user,
      roles: user.roles?.length
        ? user.roles
        : isNew
          ? [ApplicationRole.ATTORNEY]
          : user.roles,
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (!isEdit && !isNew) {
      resetForm({
        values: {
          ...user,
          password: "",
          confirmPassword: "",
        },
      });
      setSelectedImage(null);
    }
  }, [isEdit, isNew, user, resetForm]);

  const photoSrc = previewUrl;
  const useCards = isNew || !embedded;
  const fieldsLayout = useCards ? "intake" : "grid";

  const zoomPhoneValue = values.zoomPhoneNumber ?? "";
  const savedZoomUser = {
    name: values.zoomUserName,
    phoneNumber: values.zoomPhoneNumber,
    extPhoneNumber: values.zoomExtPhoneNumber,
  };
  const zoomListHasCurrent =
    !zoomPhoneValue ||
    zoomUsers.some((z) => sameZoomUser(z, savedZoomUser));

  const sortedZoomUsers = useMemo(
    () =>
      [...zoomUsers].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, {
          sensitivity: "base",
        })
      ),
    [zoomUsers]
  );

  const zoomUserOptions = useMemo(() => {
    if (!zoomPhoneValue || zoomListHasCurrent) {
      return sortedZoomUsers;
    }
    return [
      {
        name: values.zoomUserName ?? zoomPhoneValue,
        phoneNumber: zoomPhoneValue,
        extPhoneNumber: values.zoomExtPhoneNumber ?? null,
      },
      ...sortedZoomUsers,
    ];
  }, [
    sortedZoomUsers,
    zoomPhoneValue,
    zoomListHasCurrent,
    values.zoomUserName,
    values.zoomExtPhoneNumber,
  ]);

  const selectedZoomUser = useMemo(() => {
    if (!zoomPhoneValue && !savedZoomUser.name) {
      return null;
    }
    return (
      zoomUserOptions.find((z) => sameZoomUser(z, savedZoomUser)) ?? null
    );
  }, [zoomUserOptions, zoomPhoneValue, savedZoomUser.name, savedZoomUser.phoneNumber, savedZoomUser.extPhoneNumber]);

  const applyZoomUser = (user: ZoomUserResponse | null) => {
    if (!user?.phoneNumber) {
      setFieldValue("zoomPhoneNumber", null);
      setFieldValue("zoomExtPhoneNumber", null);
      setFieldValue("zoomUserName", null);
      return;
    }
    setFieldValue("zoomPhoneNumber", user.phoneNumber ?? null);
    setFieldValue("zoomExtPhoneNumber", user.extPhoneNumber ?? null);
    setFieldValue("zoomUserName", user.name ?? null);
  };

  const zoomUserField = allowRoleEdit ? (
    <Autocomplete
      fullWidth
      sx={useCards ? fullWidthFieldSx : undefined}
      options={zoomUserOptions}
      value={selectedZoomUser}
      onChange={(_, option) => applyZoomUser(option)}
      getOptionLabel={(option) =>
        option.name?.trim() || option.phoneNumber?.trim() || ""
      }
      isOptionEqualToValue={sameZoomUser}
      filterOptions={(options, { inputValue }) => {
        const query = inputValue.trim().toLowerCase();
        if (!query) {
          return options;
        }
        return options.filter(
          (option) =>
            (option.name ?? "").toLowerCase().includes(query) ||
            (option.phoneNumber ?? "").toLowerCase().includes(query) ||
            (option.extPhoneNumber ?? "").toLowerCase().includes(query)
        );
      }}
      renderOption={(props, option) => (
        <li {...props}>
          <Stack spacing={0.25}>
            <Typography variant="body2">
              {option.name?.trim() || "Unnamed user"}
            </Typography>
            {(option.phoneNumber || option.extPhoneNumber) && (
              <Typography variant="caption" color="text.secondary">
                {[option.phoneNumber, option.extPhoneNumber && `ext. ${option.extPhoneNumber}`]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            )}
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label="Zoom user" placeholder="Search by name or number…" />
      )}
    />
  ) : (
    <Box sx={useCards ? fullWidthFieldSx : undefined}>
      <ReadOnlyField
        label="Zoom user"
        value={formatZoomUserLabel(values)}
      />
    </Box>
  );

  if (!isEdit) {
    const accountBody = (
      <AccountSplit
        avatarPt={{ sm: 0.25 }}
        avatar={
          <Avatar
            src={user.photo?.url ?? undefined}
            alt={`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()}
            sx={{
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              fontSize: 44,
              fontWeight: 700,
              bgcolor: "primary.main",
            }}
          >
            {getInitials(user)}
          </Avatar>
        }
        fields={
          <Stack spacing={FIELD_GAP}>
            <ReadOnlyField label="First name" value={user.firstName} />
            <ReadOnlyField label="Last name" value={user.lastName} />
            <ReadOnlyField label="Email" value={user.email} />
            <ReadOnlyField label="Phone" value={user.phoneNumber} />
            <ReadOnlyField
              label="Zoom user"
              value={formatZoomUserLabel(user)}
            />
            <ReadOnlyField
              label="Roles"
              value={
                (user.roles?.length ?? 0) > 0 ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {(user.roles ?? []).map((role) => (
                      <StatusChip
                        key={role}
                        size="small"
                        label={ROLE_LABELS[role] ?? role}
                        tone="secondary"
                      />
                    ))}
                  </Stack>
                ) : (
                  "—"
                )
              }
            />
          </Stack>
        }
      />
    );

    const professionalBody = (
      <FieldsGrid layout={fieldsLayout}>
        <ReadOnlyField label="Law firm" value={user.companyName} />
        <ReadOnlyField
          label="Practice area"
          value={
            user.practiceArea
              ? HelpersService.camelToString(user.practiceArea)
              : undefined
          }
        />
        <ReadOnlyField label="State" value={user.state} />
        <ReadOnlyField label="City" value={user.city} />
        <Box sx={fullWidthFieldSx}>
          <ReadOnlyField label="Bio" value={user.bio} />
        </Box>
        <ReadOnlyField
          label="Year of bar admission"
          value={user.barAdmissionYear}
        />
        <ReadOnlyField
          label="Max termination age (months)"
          value={user.maxTerminationAgeMonths}
        />
        <Box sx={fullWidthFieldSx}>
          <ReadOnlyField
            label="Honors and awards"
            value={
              user.awardsAchievements?.length
                ? user.awardsAchievements.join(", ")
                : undefined
            }
          />
        </Box>
        <Box sx={fullWidthFieldSx}>
          <ReadOnlyField
            label="Bar and trade organizations"
            value={
              user.barAndTradeOrgs?.length
                ? user.barAndTradeOrgs.join(", ")
                : undefined
            }
          />
        </Box>
        <Box sx={fullWidthFieldSx}>
          <ReadOnlyField
            label="Education"
            value={
              user.education?.length ? (
                <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                  {user.education.map((edu, idx) => (
                    <li key={idx}>
                      {[edu.location, edu.course, edu.year]
                        .filter(Boolean)
                        .join(" · ")}
                    </li>
                  ))}
                </Box>
              ) : undefined
            }
          />
        </Box>
      </FieldsGrid>
    );

    if (useCards) {
      return (
        <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
          <FormSection title="Account" icon={<PersonOutlineIcon />}>
            {accountBody}
          </FormSection>
          {isAttorney && (
            <FormSection title="Professional" icon={<WorkOutlineIcon />}>
              {professionalBody}
            </FormSection>
          )}
        </Stack>
      );
    }

    return (
      <SectionsLayout
        isAttorney={isAttorney}
        account={
          <>
            <SectionTitle>Account</SectionTitle>
            {accountBody}
          </>
        }
        professional={
          <>
            <SectionTitle>Professional</SectionTitle>
            {professionalBody}
          </>
        }
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ width: "100%", minWidth: 0 }}>
      <Stack spacing={3} sx={{ width: "100%", minWidth: 0 }}>
        <input
          hidden
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          ref={inputFileRef}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            setSelectedImage(file);
            event.target.value = "";
          }}
        />

        {useCards ? (
          <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
            <FormSection title="Account" icon={<PersonOutlineIcon />}>
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "center", sm: "center" },
                    gap: 2.5,
                    pb: 0.5,
                  }}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label="Upload photo"
                    onClick={() => inputFileRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        inputFileRef.current?.click();
                      }
                    }}
                    sx={{
                      position: "relative",
                      width: 112,
                      height: 112,
                      borderRadius: "50%",
                      cursor: "pointer",
                      overflow: "hidden",
                      flexShrink: 0,
                      boxShadow: "0 0 0 3px #fff, 0 0 0 5px rgba(237, 99, 58, 0.35)",
                      "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: 3,
                      },
                    }}
                  >
                    <Avatar
                      src={photoSrc || undefined}
                      alt={`${values.firstName ?? ""} ${values.lastName ?? ""}`.trim()}
                      sx={{
                        width: 112,
                        height: 112,
                        fontSize: 36,
                        fontWeight: 700,
                        bgcolor: "primary.main",
                      }}
                    >
                      {getInitials(values)}
                    </Avatar>
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "42%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.15,
                        bgcolor: "rgba(27, 30, 51, 0.72)",
                        color: "#fff",
                      }}
                    >
                      <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.65rem",
                          lineHeight: 1,
                          color: "#fff",
                        }}
                      >
                        Upload
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: { xs: "center", sm: "left" }, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
                    >
                      Profile photo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Optional
                    </Typography>
                  </Box>
                </Box>

                <FieldsGrid layout={fieldsLayout}>
                  <FormField
                    required
                    disabled={lockStaffIdentity}
                    label="First name"
                    name="firstName"
                    value={values.firstName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!errors.firstName && !!touched.firstName}
                    helperText=""
                  />
                  <FormField
                    required
                    disabled={lockStaffIdentity}
                    label="Last name"
                    name="lastName"
                    value={values.lastName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!errors.lastName && !!touched.lastName}
                    helperText=""
                  />
                  <MuiTelInput
                    fullWidth
                    disabled={lockStaffIdentity}
                    label="Phone"
                    variant="outlined"
                    name="phoneNumber"
                    onlyCountries={["US"]}
                    defaultCountry="US"
                    disableDropdown
                    value={values.phoneNumber ?? undefined}
                    onBlur={handleBlur}
                    onChange={(newValue) =>
                      setFieldValue("phoneNumber", newValue)
                    }
                    error={!!errors.phoneNumber && !!touched.phoneNumber}
                  />
                  {!isNew && zoomUserField}
                  <FormField
                    required
                    disabled={lockEmail}
                    label="Email"
                    name="email"
                    value={values.email}
                    onBlur={handleBlur}
                    onChange={(e) =>
                      setFieldValue(
                        "email",
                        (e.target as HTMLInputElement).value.trim()
                      )
                    }
                    error={!!errors.email && !!touched.email}
                    helperText=""
                  />
                  {isNew && (
                    <>
                  <FormField
                    required
                    label="Password"
                    name="password"
                    autoComplete="new-password"
                    type={showPassword ? "text" : "password"}
                    value={values.password ?? ""}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!errors.password && !!touched.password}
                    helperText={
                      errors.password && touched.password
                        ? String(errors.password)
                        : ""
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((show) => !show)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormField
                    required
                    label="Confirm password"
                    name="confirmPassword"
                    autoComplete="new-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={values.confirmPassword ?? ""}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={
                      !!errors.confirmPassword && !!touched.confirmPassword
                    }
                    helperText={
                      errors.confirmPassword && touched.confirmPassword
                        ? String(errors.confirmPassword)
                        : ""
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() =>
                              setShowConfirmPassword((show) => !show)
                            }
                            edge="end"
                            size="small"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                    </>
                  )}
                  {!isNew &&
                    (allowRoleEdit ? (
                      <FormControl
                        fullWidth
                        required
                        error={!!errors.roles && !!touched.roles}
                        sx={fullWidthFieldSx}
                      >
                        <InputLabel id="roles-label-cards">Roles</InputLabel>
                        <Select
                          labelId="roles-label-cards"
                          label="Roles"
                          name="roles"
                          multiple
                          value={values.roles ?? []}
                          onBlur={handleBlur}
                          onChange={(e) =>
                            setFieldValue(
                              "roles",
                              typeof e.target.value === "string"
                                ? e.target.value.split(",")
                                : e.target.value
                            )
                          }
                          renderValue={(selected) =>
                            (selected as ApplicationRole[])
                              .map((role) => ROLE_LABELS[role] ?? role)
                              .join(", ")
                          }
                        >
                          {Object.values(ApplicationRole).map((role) => (
                            <MenuItem key={role} value={role}>
                              <Checkbox
                                checked={(values.roles ?? []).includes(role)}
                                size="small"
                              />
                              {ROLE_LABELS[role] ?? role}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={fullWidthFieldSx}>
                        <ReadOnlyField
                          label="Roles"
                          value={
                            (values.roles?.length ?? 0) > 0 ? (
                              <Stack
                                direction="row"
                                spacing={0.75}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {(values.roles ?? []).map((role) => (
                                  <StatusChip
                                    key={role}
                                    size="small"
                                    label={ROLE_LABELS[role] ?? role}
                                    tone="secondary"
                                  />
                                ))}
                              </Stack>
                            ) : (
                              "—"
                            )
                          }
                        />
                      </Box>
                    ))}
                </FieldsGrid>
              </Stack>
            </FormSection>

            <FormSection title="Professional" icon={<WorkOutlineIcon />}>
              <FieldsGrid layout={fieldsLayout}>
                <FormField
                  required
                  label="Law firm"
                  name="companyName"
                  value={values.companyName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!errors.companyName && !!touched.companyName}
                  helperText=""
                />
                <FormControl
                  required
                  fullWidth
                  error={!!errors.practiceArea && !!touched.practiceArea}
                >
                  <InputLabel id="practice-label-new">Practice area</InputLabel>
                  <Select
                    labelId="practice-label-new"
                    label="Practice area"
                    name="practiceArea"
                    value={values.practiceArea ?? ""}
                    onChange={handleChange}
                  >
                    {Object.keys(Category).map((key) => (
                      <MenuItem key={key} value={Category[key]}>
                        {HelpersService.camelToString(Category[key])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormField
                  required
                  label="State"
                  name="state"
                  value={values.state}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!errors.state && !!touched.state}
                  helperText=""
                />
                <FormField
                  label="City"
                  name="city"
                  value={values.city}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <Box sx={fullWidthFieldSx}>
                  <FormField
                    required
                    multiline
                    minRows={3}
                    label="Bio"
                    name="bio"
                    value={values.bio}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!errors.bio && !!touched.bio}
                    helperText=""
                  />
                </Box>
                <TextField
                  required
                  fullWidth
                  type="number"
                  label="Year of bar admission"
                  name="barAdmissionYear"
                  value={values.barAdmissionYear ?? ""}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={
                    !!errors.barAdmissionYear && !!touched.barAdmissionYear
                  }
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Max termination age (months)"
                  name="maxTerminationAgeMonths"
                  value={values.maxTerminationAgeMonths ?? ""}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <Box sx={fullWidthFieldSx}>
                  <Autocomplete
                    multiple
                    options={[]}
                    freeSolo
                    autoSelect
                    value={values.awardsAchievements ?? []}
                    onChange={(_, newValue) =>
                      setFieldValue("awardsAchievements", newValue)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Honors and awards" />
                    )}
                  />
                </Box>
                <Box sx={fullWidthFieldSx}>
                  <Autocomplete
                    multiple
                    options={BAR_AND_TRADE_ORGS}
                    freeSolo
                    autoSelect
                    disableCloseOnSelect
                    value={values.barAndTradeOrgs ?? []}
                    onChange={(_, newValue) =>
                      setFieldValue("barAndTradeOrgs", newValue)
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bar and trade organizations"
                        placeholder="Other"
                      />
                    )}
                  />
                </Box>
                <Box sx={fullWidthFieldSx}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Education
                  </Typography>
                  <EducationTable
                    initValues={values.education ?? null}
                    isEdit
                    onChange={(educationRows) =>
                      setFieldValue("education", educationRows)
                    }
                  />
                </Box>
              </FieldsGrid>
            </FormSection>
          </Stack>
        ) : (
        <SectionsLayout
          isAttorney={isAttorney}
          account={
            <>
              <SectionTitle>Account</SectionTitle>
              <AccountSplit
                avatarPt={{ sm: 1 }}
                avatar={
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label="Upload photo"
                    onClick={() => inputFileRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        inputFileRef.current?.click();
                      }
                    }}
                    sx={{
                      position: "relative",
                      width: AVATAR_SIZE,
                      height: AVATAR_SIZE,
                      borderRadius: "50%",
                      cursor: "pointer",
                      overflow: "hidden",
                      flexShrink: 0,
                      "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Avatar
                      src={photoSrc || undefined}
                      alt={`${values.firstName ?? ""} ${values.lastName ?? ""}`.trim()}
                      sx={{
                        width: AVATAR_SIZE,
                        height: AVATAR_SIZE,
                        fontSize: 44,
                        fontWeight: 700,
                        bgcolor: "primary.main",
                      }}
                    >
                      {getInitials(values)}
                    </Avatar>
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "42%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.15,
                        bgcolor: "rgba(27, 30, 51, 0.72)",
                        color: "#fff",
                      }}
                    >
                      <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.65rem",
                          lineHeight: 1,
                          color: "#fff",
                        }}
                      >
                        Upload
                      </Typography>
                    </Box>
                  </Box>
                }
                fields={
                  <Stack spacing={FIELD_GAP} sx={{ minWidth: 0, width: "100%" }}>
                    <FormField
                      required
                      disabled={lockStaffIdentity}
                      label="First name"
                      name="firstName"
                      value={values.firstName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={!!errors.firstName && !!touched.firstName}
                      helperText=""
                    />
                    <FormField
                      required
                      disabled={lockStaffIdentity}
                      label="Last name"
                      name="lastName"
                      value={values.lastName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={!!errors.lastName && !!touched.lastName}
                      helperText=""
                    />
                    <FormField
                      required
                      disabled={lockEmail}
                      label="Email"
                      name="email"
                      value={values.email}
                      onBlur={handleBlur}
                      onChange={(e) =>
                        setFieldValue(
                          "email",
                          (e.target as HTMLInputElement).value.trim()
                        )
                      }
                      error={!!errors.email && !!touched.email}
                      helperText=""
                    />
                    <MuiTelInput
                      fullWidth
                      disabled={lockStaffIdentity}
                      label="Phone"
                      variant="outlined"
                      name="phoneNumber"
                      onlyCountries={["US"]}
                      defaultCountry="US"
                      disableDropdown
                      value={values.phoneNumber ?? undefined}
                      onBlur={handleBlur}
                      onChange={(newValue) =>
                        setFieldValue("phoneNumber", newValue)
                      }
                      error={!!errors.phoneNumber && !!touched.phoneNumber}
                    />
                    {zoomUserField}
                    {allowRoleEdit ? (
                      <FormControl
                        fullWidth
                        required
                        error={!!errors.roles && !!touched.roles}
                      >
                        <InputLabel id="roles-label">Roles</InputLabel>
                        <Select
                          labelId="roles-label"
                          label="Roles"
                          name="roles"
                          multiple
                          value={values.roles ?? []}
                          onBlur={handleBlur}
                          onChange={(e) =>
                            setFieldValue(
                              "roles",
                              typeof e.target.value === "string"
                                ? e.target.value.split(",")
                                : e.target.value
                            )
                          }
                          renderValue={(selected) =>
                            (selected as ApplicationRole[])
                              .map((role) => ROLE_LABELS[role] ?? role)
                              .join(", ")
                          }
                        >
                          {Object.values(ApplicationRole).map((role) => (
                            <MenuItem key={role} value={role}>
                              <Checkbox
                                checked={(values.roles ?? []).includes(role)}
                                size="small"
                              />
                              {ROLE_LABELS[role] ?? role}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <ReadOnlyField
                        label="Roles"
                        value={
                          (values.roles?.length ?? 0) > 0 ? (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {(values.roles ?? []).map((role) => (
                                <StatusChip
                                  key={role}
                                  size="small"
                                  label={ROLE_LABELS[role] ?? role}
                                  tone="secondary"
                                />
                              ))}
                            </Stack>
                          ) : (
                            "—"
                          )
                        }
                      />
                    )}
                  </Stack>
                }
              />
            </>
          }
          professional={
            <>
              <SectionTitle>Professional</SectionTitle>
              <FieldsGrid layout={fieldsLayout}>
                <FormField
                  required
                  label="Law firm"
                  name="companyName"
                  value={values.companyName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!errors.companyName && !!touched.companyName}
                  helperText=""
                />
                <FormControl
                  required
                  fullWidth
                  error={!!errors.practiceArea && !!touched.practiceArea}
                >
                  <InputLabel id="practice-label">Practice area</InputLabel>
                  <Select
                    labelId="practice-label"
                    label="Practice area"
                    name="practiceArea"
                    value={values.practiceArea ?? ""}
                    onChange={handleChange}
                  >
                    {Object.keys(Category).map((key) => (
                      <MenuItem key={key} value={Category[key]}>
                        {HelpersService.camelToString(Category[key])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormField
                  required
                  label="State"
                  name="state"
                  value={values.state}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!errors.state && !!touched.state}
                  helperText=""
                />
                <FormField
                  label="City"
                  name="city"
                  value={values.city}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <Box sx={fullWidthFieldSx}>
                  <FormField
                    required
                    multiline
                    minRows={3}
                    label="Bio"
                    name="bio"
                    value={values.bio}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!errors.bio && !!touched.bio}
                    helperText=""
                  />
                </Box>
                <TextField
                  required
                  fullWidth
                  type="number"
                  label="Year of bar admission"
                  name="barAdmissionYear"
                  value={values.barAdmissionYear ?? ""}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={
                    !!errors.barAdmissionYear && !!touched.barAdmissionYear
                  }
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Max termination age (months)"
                  name="maxTerminationAgeMonths"
                  helperText="Leave empty for no limit"
                  value={values.maxTerminationAgeMonths ?? ""}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <Box sx={fullWidthFieldSx}>
                  <Autocomplete
                    multiple
                    options={[]}
                    freeSolo
                    autoSelect
                    value={values.awardsAchievements ?? []}
                    onChange={(_, newValue) =>
                      setFieldValue("awardsAchievements", newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Honors and awards"
                        helperText="Press Enter to add each award"
                      />
                    )}
                  />
                </Box>
                <Box sx={fullWidthFieldSx}>
                  <Autocomplete
                    multiple
                    options={BAR_AND_TRADE_ORGS}
                    freeSolo
                    autoSelect
                    disableCloseOnSelect
                    value={values.barAndTradeOrgs ?? []}
                    onChange={(_, newValue) =>
                      setFieldValue("barAndTradeOrgs", newValue)
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bar and trade organizations"
                        placeholder="Other"
                      />
                    )}
                  />
                </Box>
                <Box sx={fullWidthFieldSx}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Education
                  </Typography>
                  <EducationTable
                    initValues={values.education ?? null}
                    isEdit
                    onChange={(educationRows) =>
                      setFieldValue("education", educationRows)
                    }
                  />
                </Box>
              </FieldsGrid>
            </>
          }
        />
        )}

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={
            useCards
              ? {
                  position: "sticky",
                  bottom: 0,
                  zIndex: 2,
                  mt: 0.5,
                  mx: { xs: -0.5, md: 0 },
                  px: { xs: 0.5, md: 0 },
                  py: 1.5,
                  bgcolor: "rgba(247, 248, 251, 0.92)",
                  backdropFilter: "blur(8px)",
                  borderTop: "1px solid",
                  borderColor: "divider",
                }
              : undefined
          }
        >
          <AppButton
            variant="outlined"
            color="inherit"
            onClick={() => onCancelEdit?.()}
          >
            Cancel
          </AppButton>
          <AppButton variant="contained" color="primary" type="submit">
            {isNew ? "Create attorney" : "Save changes"}
          </AppButton>
        </Stack>
      </Stack>
    </form>
  );
}
